import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import api from "../api";
import { toast } from "react-toastify";
import {
  AlertTriangle, RefreshCw, ChevronLeft, ChevronRight,
  RotateCcw, Filter, Building2, CheckCircle2, XCircle, Clock, Loader2, X, Clipboard, ClipboardCheck
} from "lucide-react";

function RetriggerConfirmModal({ consultationId, onConfirm, onCancel }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-secondary-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center">
              <RotateCcw size={15} className="text-amber-600" />
            </div>
            <h2 className="text-sm font-bold text-secondary-800">Retrigger Summary</h2>
          </div>
          <button onClick={onCancel} className="p-1.5 rounded-xl text-secondary-400 hover:bg-secondary-100 transition-colors">
            <X size={15} />
          </button>
        </div>
        <div className="px-5 py-4 text-sm text-secondary-600">
          This will retrigger summary generation for consultation{" "}
          <span className="font-mono font-semibold text-secondary-800">#{consultationId}</span>.
          Any existing summary data may be overwritten.
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 bg-secondary-50 border-t border-secondary-100">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-secondary-600 hover:bg-secondary-200 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-xl transition-colors"
          >
            Yes, Retrigger
          </button>
        </div>
      </div>
    </div>
  );
}

const PAGE_SIZE = 50;

const STATUS_CONFIG = {
  completed: { label: "Completed", bg: "bg-emerald-100", text: "text-emerald-700" },
  processing: { label: "Processing", bg: "bg-blue-100", text: "text-blue-700" },
  failed: { label: "Failed", bg: "bg-rose-100", text: "text-rose-700" },
  not_started: { label: "Not Started", bg: "bg-secondary-100", text: "text-secondary-500" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_started;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      {status === "processing" && <Loader2 size={10} className="animate-spin" />}
      {cfg.label}
    </span>
  );
}

function BoolCell({ value }) {
  return value ? (
    <CheckCircle2 size={14} className="text-emerald-500 mx-auto" />
  ) : (
    <XCircle size={14} className="text-secondary-300 mx-auto" />
  );
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const s = iso.endsWith("Z") ? iso : `${iso}Z`;
  return new Date(s).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

const BOOL_COLUMNS = [
  { key: "mood", label: "Mood" },
  { key: "mainthemes", label: "Main Themes" },
  { key: "resistanceorengagement", label: "Resistance" },
  { key: "interventions", label: "Interventions" },
  { key: "interventionseffectiveness", label: "Interv. Effect." },
  { key: "updatedhypothesis", label: "Hypothesis" },
  { key: "sessionoutcome", label: "Outcome" },
  { key: "nextplan", label: "Next Plan" },
  { key: "selfreflection", label: "Self Refl." },
  { key: "summary", label: "Summary" },
  { key: "what_went_well", label: "Went Well" },
  { key: "supervisor_evaluation", label: "Supervisor Eval." },
];

export default function AdminConsultations() {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [orgs, setOrgs] = useState([]);
  const [filters, setFilters] = useState({ org_id: "", status: "" });
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [retriggering, setRetriggering] = useState({});
  const [confirmId, setConfirmId] = useState(null);
  const [copyingSignals, setCopyingSignals] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  const fetchOrgs = useCallback(async () => {
    try {
      const res = await api.getAdminConsultationOrgs();
      setOrgs(res.data.orgs || []);
    } catch (err) {
      if (err.response?.status === 403) setAccessDenied(true);
    }
  }, []);

  const fetchData = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (filters.org_id) params.org_id = filters.org_id;
      if (filters.status) params.status = filters.status;

      const res = await api.getAdminConsultationsOverview(params);
      setRows(res.data.data || []);
      setPagination({
        page: res.data.page,
        total: res.data.total,
        pages: res.data.pages,
      });
    } catch (err) {
      if (err.response?.status === 403) setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleCopyRawSignals = async (id) => {
    setCopyingSignals((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await api.getAdminConsultationRawSignals(id);
      const text = JSON.stringify(res.data, null, 2);
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success(`Raw signals copied for #${id}`);
      setTimeout(() => setCopiedId((prev) => (prev === id ? null : prev)), 2000);
    } catch (err) {
      // toast handled by global interceptor or clipboard failure
      toast.error("Failed to copy raw signals");
    } finally {
      setCopyingSignals((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleRetriggerConfirmed = async () => {
    const id = confirmId;
    setConfirmId(null);
    setRetriggering((prev) => ({ ...prev, [id]: true }));
    try {
      await api.retriggerConsultationSummary(id);
      toast.success(`Summary generation retriggered for consultation #${id}`);
      setTimeout(() => fetchData(pagination.page), 1500);
    } catch (err) {
      // toast handled by global interceptor
    } finally {
      setRetriggering((prev) => ({ ...prev, [id]: false }));
    }
  };

  if (accessDenied) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-96 gap-4 text-center">
          <AlertTriangle size={48} className="text-rose-400" />
          <h2 className="text-xl font-bold text-secondary-800">Access Restricted</h2>
          <p className="text-secondary-500 max-w-md">
            Consultations overview is only accessible to users belonging to the admin organisation.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const start = (pagination.page - 1) * PAGE_SIZE + 1;
  const end = Math.min(pagination.page * PAGE_SIZE, pagination.total);

  return (
    <DashboardLayout>
      <div className="px-4 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-secondary-800">Consultations Overview</h1>
            <p className="text-sm text-secondary-400 mt-0.5">
              All non-admin-org consultations with summary generation status
            </p>
          </div>
          <button
            onClick={() => fetchData(pagination.page)}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-secondary-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-colors disabled:opacity-50"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white/70 backdrop-blur-sm border border-white/30 rounded-2xl shadow-sm p-4 flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2 text-secondary-400 text-sm font-medium">
            <Filter size={15} />
            Filters
          </div>

          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="text-xs font-semibold text-secondary-400 uppercase tracking-wide flex items-center gap-1">
              <Building2 size={11} /> Organisation
            </label>
            <select
              value={filters.org_id}
              onChange={(e) => handleFilterChange("org_id", e.target.value)}
              className="text-sm border border-secondary-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              <option value="">All organisations</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="text-xs font-semibold text-secondary-400 uppercase tracking-wide flex items-center gap-1">
              <Clock size={11} /> Summary Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="text-sm border border-secondary-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              <option value="">All statuses</option>
              <option value="not_started">Not Started</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {(filters.org_id || filters.status) && (
            <button
              onClick={() => setFilters({ org_id: "", status: "" })}
              className="text-xs text-secondary-400 hover:text-rose-500 transition-colors mt-4"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white/70 backdrop-blur-sm border border-white/30 rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-40 gap-3 text-secondary-400">
              <Loader2 size={20} className="animate-spin" />
              <span className="text-sm">Loading consultations…</span>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2 text-secondary-400">
              <CheckCircle2 size={28} className="text-secondary-200" />
              <p className="text-sm">No consultations found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-secondary-50/80 border-b border-secondary-100">
                    {/* Fixed columns */}
                    <th className="px-3 py-3 text-left font-semibold text-secondary-500 whitespace-nowrap">ID</th>
                    <th className="px-3 py-3 text-left font-semibold text-secondary-500 whitespace-nowrap">Org ID</th>
                    <th className="px-3 py-3 text-left font-semibold text-secondary-500 whitespace-nowrap">Org Name</th>
                    <th className="px-3 py-3 text-left font-semibold text-secondary-500 whitespace-nowrap">Therapy Type</th>
                    <th className="px-3 py-3 text-left font-semibold text-secondary-500 whitespace-nowrap">Session Date</th>
                    <th className="px-3 py-3 text-left font-semibold text-secondary-500 whitespace-nowrap">Eval. Standard</th>
                    <th className="px-3 py-3 text-left font-semibold text-secondary-500 whitespace-nowrap">Gen. Status</th>
                    <th className="px-3 py-3 text-left font-semibold text-secondary-500 whitespace-nowrap">Gen. Start</th>
                    <th className="px-3 py-3 text-left font-semibold text-secondary-500 whitespace-nowrap">Gen. End</th>
                    <th className="px-3 py-3 text-center font-semibold text-secondary-500 whitespace-nowrap">Retries</th>
                    {/* Bool columns */}
                    {BOOL_COLUMNS.map((col) => (
                      <th key={col.key} className="px-2 py-3 text-center font-semibold text-secondary-500 whitespace-nowrap">
                        {col.label}
                      </th>
                    ))}
                    <th className="px-3 py-3 text-center font-semibold text-secondary-500 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100">
                  {rows.map((row) => (
                    <tr key={row.id} className="hover:bg-white/60 transition-colors">
                      <td className="px-3 py-2.5 font-mono text-secondary-700">#{row.id}</td>
                      <td className="px-3 py-2.5 font-mono text-secondary-500">{row.org_id}</td>
                      <td className="px-3 py-2.5 text-secondary-700 font-medium whitespace-nowrap max-w-[140px] truncate" title={row.org_name}>
                        {row.org_name || "—"}
                      </td>
                      <td className="px-3 py-2.5 text-secondary-600">{row.therapy_type || "—"}</td>
                      <td className="px-3 py-2.5 text-secondary-600 whitespace-nowrap">{row.session_date || "—"}</td>
                      <td className="px-3 py-2.5 text-secondary-600">{row.evaluation_standard || "—"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <StatusBadge status={row.summary_generation_status} />
                      </td>
                      <td className="px-3 py-2.5 text-secondary-500 whitespace-nowrap font-mono text-[10px]">
                        {formatDateTime(row.summary_generation_start_time)}
                      </td>
                      <td className="px-3 py-2.5 text-secondary-500 whitespace-nowrap font-mono text-[10px]">
                        {formatDateTime(row.summary_generation_end_time)}
                      </td>
                      <td className="px-3 py-2.5 text-center text-secondary-600 font-mono">
                        {row.summary_generation_retry_count}
                      </td>
                      {BOOL_COLUMNS.map((col) => (
                        <td key={col.key} className="px-2 py-2.5 text-center">
                          <BoolCell value={row[col.key]} />
                        </td>
                      ))}
                      <td className="px-3 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => setConfirmId(row.id)}
                            disabled={
                              retriggering[row.id] ||
                              row.summary_generation_status === "processing"
                            }
                            title="Retrigger summary generation"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary-600 hover:text-white hover:bg-primary-500 border border-primary-200 hover:border-primary-500 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {retriggering[row.id] ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <RotateCcw size={12} />
                            )}
                            Retrigger
                          </button>
                          <button
                            onClick={() => handleCopyRawSignals(row.id)}
                            disabled={copyingSignals[row.id]}
                            title="Copy raw signals to clipboard"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-secondary-600 hover:text-white hover:bg-secondary-500 border border-secondary-200 hover:border-secondary-500 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {copyingSignals[row.id] ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : copiedId === row.id ? (
                              <ClipboardCheck size={12} className="text-emerald-500" />
                            ) : (
                              <Clipboard size={12} />
                            )}
                            {copiedId === row.id ? "Copied!" : "Signals"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && rows.length > 0 && (
          <div className="flex items-center justify-between text-sm text-secondary-500">
            <span>
              Showing {start}–{end} of {pagination.total} consultations
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchData(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="p-2 rounded-xl hover:bg-secondary-100 disabled:opacity-30 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="px-3 py-1.5 rounded-xl bg-white border border-secondary-200 font-mono text-xs">
                {pagination.page} / {pagination.pages}
              </span>
              <button
                onClick={() => fetchData(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="p-2 rounded-xl hover:bg-secondary-100 disabled:opacity-30 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {confirmId !== null && (
        <RetriggerConfirmModal
          consultationId={confirmId}
          onConfirm={handleRetriggerConfirmed}
          onCancel={() => setConfirmId(null)}
        />
      )}
    </DashboardLayout>
  );
}
